import { useTranslate } from "ra-core";
import { useState } from "react";

import { safeHref } from "@/components/market-intel/markdown";
import type { MarketCompetitor } from "@/components/market-intel/types";
import { cn } from "@/lib/utils";

import { initialOf } from "./intel";

/** http(s) URLs, or an inline image (the demo's logos); nothing else. */
const logoSrc = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (/^data:image\/(svg\+xml|png|jpeg|webp)[;,]/i.test(url)) return url;
  return safeHref(url);
};

/** Soft tints for the initial; picked from the name so it never changes. */
const TINTS = [
  "bg-sky-500/15 text-sky-800 dark:bg-sky-400/20 dark:text-sky-100",
  "bg-emerald-500/15 text-emerald-800 dark:bg-emerald-400/20 dark:text-emerald-100",
  "bg-violet-500/15 text-violet-800 dark:bg-violet-400/20 dark:text-violet-100",
  "bg-amber-500/18 text-amber-900 dark:bg-amber-400/20 dark:text-amber-100",
  "bg-rose-500/15 text-rose-800 dark:bg-rose-400/20 dark:text-rose-100",
  "bg-teal-500/15 text-teal-800 dark:bg-teal-400/20 dark:text-teal-100",
];

const tintOf = (name: string): string =>
  TINTS[
    [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0) % TINTS.length
  ];

/** The competitor's logo, or its initial on a tint when it has none / fails. */
export const CompetitorAvatar = ({
  competitor,
  size = "md",
  className,
}: {
  competitor: Pick<MarketCompetitor, "name" | "logo_url">;
  size?: "md" | "lg";
  className?: string;
}) => {
  const translate = useTranslate();
  const [failed, setFailed] = useState(false);
  const logo = logoSrc(competitor.logo_url);
  const box = cn(
    "flex shrink-0 items-center justify-center overflow-hidden ring-1 ring-black/5 ring-inset dark:ring-white/10",
    size === "lg"
      ? "size-16 rounded-[1.25rem] text-2xl"
      : "size-11 rounded-2xl text-lg",
    className,
  );
  if (logo && !failed)
    return (
      <img
        src={logo}
        alt={translate("competition.list.logo_of", { name: competitor.name })}
        loading="lazy"
        onError={() => setFailed(true)}
        className={cn(box, "bg-white object-contain")}
      />
    );
  return (
    <span aria-hidden className={cn(box, "font-bold", tintOf(competitor.name))}>
      {initialOf(competitor.name)}
    </span>
  );
};
